import { call, put, take, takeEvery, all, select } from "redux-saga/effects";
import { actions as routerActions } from "redux-router5";
import { getFormValues, stopSubmit, change } from "redux-form";
import * as FB from "modules/auth/facebook-effects";
import BrandHelper from "../brand/brand-helper";

import Actions from "modules/auth/auth-actions";
import {
  withErrorHandlingNoRethrow,
  composeSaga
} from "modules/root/saga-helpers";
import { delay } from "redux-saga";
import * as Sentry from "@sentry/browser";

import * as API from "modules/auth/auth-api-saga";
import * as UserAPI from "modules/user/user-api-saga";
import { getItem } from "modules/local-storage/local-storage-effects";
import { APP_TOKEN_KEY } from "modules/user/user-constants";
import { setAppToken } from "modules/user/user-utils";
import UserActions from "modules/user/user-actions";
import CommonActions from "modules/common/common-actions";

import {
  CONFIRM_EMAIL,
  CREATE_PASSWORD,
  SIGN_UP_FINISHED,
  GUIDANCE_SELF_DIAGNOSTICS
} from "modules/routing/routes";
import { validateConfirmEmail } from "modules/utils/validations";
import { FORM_NAME as createAccountFormName } from "modules/auth/create-account-form";
import { FORM_NAME as confirmEmailFormName } from "modules/auth/confirm-email-form";
import { FORM_NAME as bankIdFrom } from "modules/general-info/bankid-login-form";
import { ModalType } from "modules/modal/modal-constants";
import { runModal } from "modules/modal/modal-saga";
import { getRefreshToken, getConsent } from "modules/user/user-selectors";
import constants from "../../common/constants";

import { getLoginMethod } from "./auth-selectors";
import { getBrand } from "../brand/brand-selectors";
import {
  BIRTHDAY_FIELD_NAME,
  FORM_NAME as sexFormName,
  SEX_FIELD_NAME
} from "../general-info/sex-and-age-form";
import { FORM_NAME as ssnFormName } from "../general-info/bankid-login-form";
import moment from "moment";

function* requestLogin() {
  const { login, password } = yield call(runModal, ModalType.TEMP_LOGIN);
  const resp = yield call(API.login, login, password);
  return resp.data.data;
}

function setSentryUser(user) {
  Sentry.setUser({ id: user.user_id });
  console.log("Sentry user set", user.user_id || "[empty]");
}

export function* loginUser() {
  // // TODO: STORE KEY PROPERLY
  try {
    const resp = yield call(UserAPI.getLogin);
    const refreshToken = resp.headers["refresh-token"];
    const appToken = resp.headers[APP_TOKEN_KEY.toLowerCase()];
    yield call(setAppToken, appToken);
    const user = resp.data.data;
    yield call(setSentryUser, user);
    const brand = yield select(getBrand);
    const language = BrandHelper.getDefaultLanguage(brand) || user.language;

    return yield put(
      UserActions.Creators.userFetched({
        refreshToken,
        ...user,
        language
      })
    );
  } catch (err) {
    const storedKey = getItem(APP_TOKEN_KEY);
    yield call(setAppToken, storedKey);
    const user = yield call(requestLogin);
    return yield put(UserActions.Creators.userFetched(user));
  }
}

export function* signUpUser({ values }) {
  const {
    first_name,
    last_name,
    email,
    clinic_city,
    customer_id,
    language,
    phone
  } = values;
  try {
    const formValues = yield select(getFormValues(bankIdFrom));
    const refreshToken = yield select(getRefreshToken);
    const ssn = formValues && formValues.ssn;

    yield put(CommonActions.Creators.startUiBlockingProcess("signUp"));

    yield call(
      API.signUpUser,
      email,
      first_name,
      last_name,
      refreshToken,
      language,
      clinic_city,
      customer_id,
      ssn,
      phone
    );
    const brand = yield select(getBrand);
    const loginMethod = yield select(getLoginMethod);
    if (
      brand.registrationWithEmailEnabled &&
      brand.emailVerificationEnabled &&
      loginMethod === constants.LoginMethod.Email
    ) {
      yield put(routerActions.navigateTo(CONFIRM_EMAIL, { email }));
    } else {
      yield put(routerActions.navigateTo(SIGN_UP_FINISHED));
    }
  } catch (err) {
    const status = err.response.status;
    if (status === 403) {
      yield put(
        stopSubmit(createAccountFormName, {
          email: "sign-up.create-account.code_already_sent"
        })
      );
    } else if (status === 406) {
      yield put(
        stopSubmit(createAccountFormName, {
          email: "sign-up.create-account.wrong_email"
        })
      );
    }
  } finally {
    yield put(CommonActions.Creators.endUiBlockingProcess("signUp"));
  }
}

export function* getSsnKey({ values, setLoader, setQrCodes, setToken }) {
  try {
    const res = yield call(API.getSsnKey, values.ssn);

    const order_ref = res.data && res.data.data.order_ref;
    setToken(res.data.data.auto_start_token);
    setQrCodes(res.data.data.qr_codes);

    if (order_ref) yield call(bankIdCollect, order_ref, setLoader, values.ssn);
  } catch (err) {
    const status = err.response.status;
    setLoader(false);
    if (status === 406)
      yield put(
        stopSubmit(bankIdFrom, {
          ssn: "bank_id.error_406"
        })
      );
    else {
      yield put(
        stopSubmit(bankIdFrom, {
          ssn: "bank_id.error_400"
        })
      );
    }
  }
}

export function* bankIdCollect(order_ref, setLoader, personalNumber) {
  const data = yield call(API.bankIdCollect, order_ref, personalNumber);
  if (data.status === 200) {
    setLoader(false);
    yield put(Actions.Creators.setLoginMethod(constants.LoginMethod.BankId));
    yield put(
      change(
        sexFormName,
        SEX_FIELD_NAME,
        constants.COLLECT_SEX_MAP[data.data.data.sex],
        true
      )
    );
    yield put(
      change(
        sexFormName,
        BIRTHDAY_FIELD_NAME,
        moment(data.data.data.birth, "YYYY-MM-DD")
          .toDate()
          .toString(),
        true
      )
    );
    yield put(change(ssnFormName, "ssn", data.data.data.ssn, true));
    return yield put(routerActions.navigateTo(GUIDANCE_SELF_DIAGNOSTICS));
  } else if (data.status === 202) {
    yield call(delay, 5000);
    yield call(bankIdCollect, order_ref, setLoader, personalNumber);
  } else {
    setLoader(false);
    yield put(
      stopSubmit(bankIdFrom, {
        ssn: "bank_id.error_400"
      })
    );
  }
}

function* onEmailConfirmed({ values: { email, code } }) {
  let passToken = "";
  yield put(CommonActions.Creators.startUiBlockingProcess("confirm"));
  try {
    const consent = yield select(getConsent);
    const resp = yield call(API.confirmEmail, email, code, consent);
    passToken = resp.data["Pass-Token"];
    yield put(routerActions.navigateTo(CREATE_PASSWORD));
  } catch (err) {
    // Wrongly passed code
    console.warn(err);
    return yield put(
      stopSubmit(confirmEmailFormName, { code: "sign-up.error.code" })
    );
  } finally {
    yield put(CommonActions.Creators.endUiBlockingProcess("confirm"));
  }

  while (true) {
    try {
      const {
        values: { password1: password }
      } = yield take(Actions.Types.PASSWORD_CREATED);
      yield put(CommonActions.Creators.startUiBlockingProcess("password"));
      yield call(API.updatePassword, email, password, passToken);
      return yield put(routerActions.navigateTo(SIGN_UP_FINISHED));
    } catch (err) {
      console.warn(err);
      // no need to rethrow - pass-token is reusable
    } finally {
      yield put(CommonActions.Creators.endUiBlockingProcess("password"));
    }
  }
}

function* onResendEmailCode() {
  const formValues = yield select(getFormValues(confirmEmailFormName));
  const errors = validateConfirmEmail(formValues || {});
  if (errors && errors.email) {
    // User must enter valid email, otherwise we display email error on the form
    return yield put(stopSubmit(confirmEmailFormName, errors));
  }
  try {
    yield put(CommonActions.Creators.startUiBlockingProcess("resendEmail"));
    yield call(API.resendConfirmEmail, formValues.email);
  } finally {
    yield put(CommonActions.Creators.endUiBlockingProcess("resendEmail"));
  }
}

function* onFacebookSignUp(token) {
  let fbToken = token;
  if (!fbToken) {
    fbToken = yield call(FB.login);
  }
  yield call(API.fbLogin, fbToken);
  return yield put(routerActions.navigateTo(SIGN_UP_FINISHED));
}

function* initializeFacebook() {
  // Init also checks the status of the user therefore returns accessToken
  const token = yield call(FB.init);
  while (true) {
    yield take(Actions.Types.SIGN_UP_FACEBOOK);
    yield call(onFacebookSignUp, token);
  }
}

export default function* userSaga() {
  // don't need FB for now
  // yield fork(composeSaga(withErrorHandlingNoRethrow, initializeFacebook));

  yield all([
    takeEvery(
      Actions.Types.SIGN_UP,
      composeSaga(withErrorHandlingNoRethrow, signUpUser)
    ),
    takeEvery(
      Actions.Types.GET_SSN_KEY,
      composeSaga(withErrorHandlingNoRethrow, getSsnKey)
    ),
    takeEvery(
      Actions.Types.EMAIL_CONFIRMED,
      composeSaga(withErrorHandlingNoRethrow, onEmailConfirmed)
    ),
    takeEvery(
      Actions.Types.RESEND_EMAIL_CODE,
      composeSaga(withErrorHandlingNoRethrow, onResendEmailCode)
    )
  ]);
}



// WEBPACK FOOTER //
// ./src/modules/auth/auth-saga.js